import { Component, Input, OnChanges, OnDestroy, ChangeDetectionStrategy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GameService } from '../../core/state/game.service';
import { BackendStaffMember } from '../../core/models/types';
import { staffUpgradesApi, staffRequest, StaffQuote, StaffRequest, StaffOperation, StaffClubStatus } from '../../core/http/staff-upgrades.api';
import { IconComponent } from '../../shared/ui/icon/icon.component';

interface SavedRequest {
  clubId: string;
  staffId: string;
  body: StaffRequest;
}

export interface StaffDepartmentMeta {
  roleName: string;
  areaName: string;
  icon: string;
  benefit: string;
  lineName: string;
  badgeText: string;
}

@Component({
  selector: 'app-staff-panel',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './staff-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaffPanelComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) clubId = '';
  readonly game = inject(GameService);

  readonly members = signal<BackendStaffMember[]>([]);
  readonly status = signal<StaffClubStatus | null>(null);
  readonly quote = signal<StaffQuote | null>(null);
  readonly quotingMemberId = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly loading = signal(false);
  readonly submitting = signal(false);
  readonly retry = signal(false);
  readonly lastOperation = signal<StaffOperation | null>(null);
  readonly availableBalance = signal<number | null>(null);

  private saved: SavedRequest | null = null;
  private destroyed = false;
  private generation = 0;

  // --- COMPUTED KPIs ---
  readonly averageRating = computed<number>(() => {
    const list = this.members();
    if (!list.length) return 0;
    const sum = list.reduce((acc, m) => acc + (m.starRating || 0), 0);
    return Math.round((sum / list.length) * 10) / 10;
  });

  readonly averageModifier = computed<number>(() => {
    const list = this.members();
    if (!list.length) return 0;
    const sum = list.reduce((acc, m) => acc + (m.performanceModifierPct || 0), 0);
    return Math.round((sum / list.length) * 10) / 10;
  });

  readonly totalAnnualSalary = computed<number>(() => {
    return this.members().reduce((acc, m) => acc + (m.annualCostReference || 0), 0);
  });

  readonly isSlotAvailable = computed<boolean>(() => {
    return this.status()?.slot?.availability === 'AVAILABLE';
  });

  readonly isSlotLimitReached = computed<boolean>(() => {
    return this.status()?.slot?.availability === 'LIMIT_REACHED';
  });

  readonly completionMatchday = computed<number | null>(() => {
    return this.status()?.slot?.completionMatchdayNumber ?? null;
  });

  ngOnChanges(): void {
    this.generation++;
    this.members.set([]);
    this.status.set(null);
    this.quote.set(null);
    this.quotingMemberId.set(null);
    this.lastOperation.set(null);
    this.availableBalance.set(null);
    this.loading.set(false);
    this.submitting.set(false);
    this.retry.set(false);
    this.saved = null;

    try {
      const saved = JSON.parse(sessionStorage.getItem(this.key()) || 'null') as SavedRequest | null;
      if (saved?.clubId === this.clubId && saved.staffId && saved.body?.operationId && saved.body.ruleSetVersion) {
        this.saved = saved;
        this.retry.set(true);
      }
    } catch {
      // Storage unavailable: fallback smoothly
    }
    void this.refresh();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.generation++;
  }

  private current(id: string, generation: number): boolean {
    return !this.destroyed && this.clubId === id && this.generation === generation;
  }

  private key(): string {
    return `lom.staff.request.${this.clubId}`;
  }

  private remember(request: SavedRequest | null): void {
    this.saved = request;
    try {
      if (request) sessionStorage.setItem(this.key(), JSON.stringify(request));
      else sessionStorage.removeItem(this.key());
    } catch {
      // A storage failure must not change the result of a purchase
    }
  }

  async refresh(): Promise<void> {
    if (!this.clubId || this.loading() || this.submitting() || this.destroyed) return;
    const id = this.clubId;
    const generation = this.generation;
    this.loading.set(true);

    try {
      const status = await staffUpgradesApi.status(id);
      if (!this.current(id, generation)) return;
      if (status.clubId !== id || !Array.isArray(status.activeUpgrades) || !status.slot) {
        throw new Error('Respuesta de estado incompleta');
      }
      this.status.set(status);

      if (this.saved) {
        try {
          const op = await staffUpgradesApi.operation(id, this.saved.staffId, this.saved.body.operationId);
          if (!this.current(id, generation)) return;
          this.accept(op);
        } catch (e: unknown) {
          if (!this.current(id, generation)) return;
          if (this.code(e) !== 404) throw e;
          this.retry.set(true);
        }
      }

      const members = await this.game.fetchClubStaff(id, true);
      if (!this.current(id, generation)) return;
      if (!members) throw new Error('No se pudo cargar el personal técnico');
      this.members.set(members);

      const balance = await this.game.fetchClubBalance(id, true);
      if (!this.current(id, generation)) return;
      if (!balance) throw new Error('No se pudo sincronizar la tesorería');
      this.availableBalance.set(balance.availableBalance);
      this.error.set(null);
    } catch {
      if (this.current(id, generation)) {
        this.error.set('No se pudo sincronizar el cuerpo técnico. Reintenta la conexión antes de contratar una mejora.');
      }
    } finally {
      if (this.current(id, generation)) this.loading.set(false);
    }
  }

  getDepartmentMeta(type: string): StaffDepartmentMeta {
    switch (type) {
      case 'GOALKEEPER_COACH':
        return {
          roleName: this.game.t('stadium.staff_gk'),
          areaName: 'Portería',
          icon: 'shield',
          benefit: 'Mejora el rendimiento de la portería en cada partido.',
          lineName: 'Porteros',
          badgeText: 'Portería',
        };
      case 'PHYSICAL_COACH':
        return {
          roleName: this.game.t('stadium.staff_fitness'),
          areaName: 'Defensa',
          icon: 'activity',
          benefit: 'Mejora el rendimiento de la defensa en cada partido.',
          lineName: 'Defensa',
          badgeText: 'Defensa',
        };
      case 'TECHNICAL_COACH':
        return {
          roleName: this.game.t('stadium.staff_tech'),
          areaName: 'Mediocampo',
          icon: 'zap',
          benefit: 'Mejora el rendimiento del mediocampo en cada partido.',
          lineName: 'Mediocampo',
          badgeText: 'Mediocampo',
        };
      case 'SHOOTING_COACH':
      default:
        return {
          roleName: this.game.t('stadium.staff_shooting'),
          areaName: 'Delantera',
          icon: 'flame',
          benefit: 'Mejora el rendimiento de la delantera en cada partido.',
          lineName: 'Delantera',
          badgeText: 'Delantera',
        };
    }
  }

  active(staffId: string) {
    return this.status()?.activeUpgrades.find(item => item.operation.staffMemberId === staffId);
  }

  canQuote(member: BackendStaffMember): boolean {
    return (
      !!this.status() &&
      this.status()!.slot.availability === 'AVAILABLE' &&
      !this.error() &&
      !this.loading() &&
      !this.submitting() &&
      !this.quotingMemberId() &&
      !this.retry() &&
      member.status === 'ACTIVE' &&
      member.starRating < 5 &&
      !this.active(member.id)
    );
  }

  role(type: string): string {
    return this.getDepartmentMeta(type).roleName;
  }

  increment(member: BackendStaffMember): number {
    return Math.min(5 - member.starRating, 1);
  }

  nextRating(member: BackendStaffMember): number {
    return Math.min(5, member.starRating + this.increment(member));
  }

  projectedModifier(member: BackendStaffMember): number {
    return member.performanceModifierPct + this.increment(member);
  }

  getStarStates(rating: number): Array<'full' | 'half' | 'empty'> {
    const states: Array<'full' | 'half' | 'empty'> = [];
    for (let i = 0; i < 5; i++) {
      if (rating >= i + 1) {
        states.push('full');
      } else if (rating >= i + 0.5) {
        states.push('half');
      } else {
        states.push('empty');
      }
    }
    return states;
  }

  canAffordQuote(): boolean {
    const quote = this.quote();
    const balance = this.availableBalance();
    return !!quote && balance !== null && balance >= quote.quotedCost;
  }

  money(value: number): string {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  }

  moneyM(value: number): string {
    if (value >= 1_000_000) {
      return (value / 1_000_000).toFixed(2) + ' M€';
    }
    return this.money(value);
  }

  operationLabel(status: StaffOperation['status']): string {
    return {
      REQUESTED: 'Procesando pago de formación',
      FUNDS_CONFIRMED: 'Confirmando partida presupuestaria',
      CAPTURE_PENDING: 'Confirmando pago bancario',
      FUNDS_CAPTURED: 'Formación técnica en curso',
      REVERSAL_PENDING: 'Procesando devolución',
      CANCEL_REQUESTED: 'Cancelación en curso',
      APPLIED: 'Mejora completada y activa',
      FAILED: 'Mejora no realizada. Revisa el saldo y el cupo.',
      CANCELLED: 'Mejora cancelada',
    }[status] || status;
  }

  slotLabel(): string {
    const slot = this.status()?.slot;
    if (!slot) return 'Consultando disponibilidad de cupo…';
    switch (slot.availability) {
      case 'AVAILABLE':
        return `Cupo disponible · 1 mejora hasta final de jornada ${slot.completionMatchdayNumber}`;
      case 'LIMIT_REACHED':
        return `Cupo de jornada ${slot.completionMatchdayNumber} agotado · Se renovará en la próxima jornada`;
      case 'NO_CALENDAR':
        return 'Sin jornada oficial activa para contratar formación.';
      case 'CALENDAR_UNAVAILABLE':
        return 'Calendario no disponible temporalmente. Vuelve a consultar más tarde.';
      default:
        return 'Consultando cupo de jornada…';
    }
  }

  private code(e: unknown): number | undefined {
    return (e as { status?: number })?.status;
  }

  async requestQuote(member: BackendStaffMember): Promise<void> {
    if (!this.canQuote(member)) return;
    const id = this.clubId;
    const generation = this.generation;
    this.quotingMemberId.set(member.id);
    this.quote.set(null);

    try {
      const q = await staffUpgradesApi.quote(id, member.id);
      if (!this.current(id, generation)) return;
      if (q.clubId !== id || q.staffMemberId !== member.id || !Number.isFinite(q.quotedCost) || !Number.isFinite(q.toRating)) {
        throw new Error('Presupuesto de formación incompleto');
      }
      this.quote.set(q);
    } catch (e) {
      if (this.current(id, generation)) {
        this.error.set(
          this.code(e) === 409
            ? 'El preparador o el presupuesto ha cambiado. Actualiza la sección para consultar de nuevo.'
            : 'No se pudo obtener el presupuesto oficial de mejora.'
        );
      }
    } finally {
      if (this.current(id, generation)) {
        this.quotingMemberId.set(null);
      }
    }
  }

  closeQuoteModal(): void {
    if (this.submitting()) return;
    this.quote.set(null);
  }

  async confirm(): Promise<void> {
    const q = this.quote();
    if (
      !q ||
      !this.canAffordQuote() ||
      this.loading() ||
      this.submitting() ||
      this.retry() ||
      this.error() ||
      this.status()?.slot.availability !== 'AVAILABLE'
    ) {
      return;
    }
    const request = {
      clubId: this.clubId,
      staffId: q.staffMemberId,
      body: staffRequest(q, crypto.randomUUID()),
    };
    this.remember(request);
    await this.send(request);
  }

  async retryRequest(): Promise<void> {
    if (this.saved && this.retry() && !this.loading() && !this.submitting()) {
      await this.send(this.saved);
    }
  }

  private accept(op: StaffOperation): void {
    if (!op.id || !op.status) throw new Error('Respuesta incompleta');
    this.lastOperation.set(op);
    this.retry.set(false);
    this.quote.set(null);
    if (['APPLIED', 'FAILED', 'CANCELLED'].includes(op.status)) {
      this.remember(null);
    }
  }

  private async send(request: SavedRequest): Promise<void> {
    const id = this.clubId;
    const generation = this.generation;
    this.submitting.set(true);
    this.error.set(null);

    try {
      const op = await staffUpgradesApi.request(id, request.staffId, request.body);
      if (!this.current(id, generation)) return;
      this.accept(op);
    } catch (e) {
      if (!this.current(id, generation)) return;
      const status = this.code(e);
      if (status && [400, 403, 404, 409, 422].includes(status)) {
        this.remember(null);
        this.retry.set(false);
        this.quote.set(null);
        this.error.set(
          status === 409
            ? 'El cupo de la jornada o el presupuesto ha cambiado. Actualiza la vista para ver el estado actual.'
            : 'No se pudo contratar la mejora. Actualiza el club para recargar los datos.'
        );
      } else {
        this.retry.set(true);
        this.error.set('No se pudo confirmar la transacción. Reintenta la misma solicitud para evitar cobros duplicados.');
      }
      return;
    } finally {
      if (this.current(id, generation)) {
        this.submitting.set(false);
      }
    }
    await this.refresh();
  }
}
